import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, LANGUAGES, GENRES, DEITY_NAMES, TITLE_CASE_FIELDS, toTitleCase } from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import { listDrafts, upsertDraft, loadDraft, removeDraft } from "../lib/idb";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, FileAudio, X, AlertCircle, Instagram, Save, Trash2, ChevronDown, FilePlus, RefreshCw, Info } from "lucide-react";

export default function UploadSong() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    song_title: "", artist_name: "",
    primary_artist_name: "", primary_artist_instagram: "", primary_artist_spotify: "", primary_artist_apple_music: "",
    singer_name: "", composer_name: "", lyricist_name: "", music_director: "", label_name: "",
    deity_name: "", language: "", genre: "", release_date: "", email: user?.email || "", notes: "",
  });
  const [artwork, setArtwork] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState("");
  const [artworkError, setArtworkError] = useState("");
  const [audio, setAudio] = useState(null);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const artInput = useRef(null);
  const audInput = useRef(null);
  const fieldRefs = useRef({});

  const refreshDrafts = async () => setDrafts(await listDrafts());

  useEffect(() => { refreshDrafts(); }, []);

  // mark dirty on any change to form/files
  useEffect(() => { setDirty(true); }, [form, artwork, audio]);

  const applyDraft = async (id) => {
    const d = await loadDraft(id);
    if (!d) { toast.error("Draft not found"); await refreshDrafts(); return; }
    if (d.form) setForm((f) => ({ ...f, ...d.form }));
    if (d.artwork instanceof Blob) {
      setArtwork(d.artwork);
      setArtworkPreview(URL.createObjectURL(d.artwork));
    } else { setArtwork(null); setArtworkPreview(""); }
    setAudio(d.audio instanceof Blob ? d.audio : null);
    setCurrentDraftId(id);
    const meta = (await listDrafts()).find((x) => x.id === id);
    setDraftName(meta?.name || "");
    setLastSaved(d.saved_at || null);
    setDirty(false);
    toast.info(`Draft loaded: ${meta?.name || id}`);
  };

  const saveDraft = async (silent = false) => {
    if (!silent) setSavingDraft(true);
    try {
      const id = await upsertDraft({
        id: currentDraftId, name: draftName || undefined,
        form, artwork, audio,
      });
      setCurrentDraftId(id);
      setLastSaved(Date.now());
      setDirty(false);
      await refreshDrafts();
      if (!silent) toast.success("Draft saved");
    } catch { if (!silent) toast.error("Could not save draft"); }
    finally { if (!silent) setSavingDraft(false); }
  };

  // Auto-save every 15s when a draft is active and there are unsaved changes
  useEffect(() => {
    if (!currentDraftId) return;
    const t = setInterval(() => { if (dirty) saveDraft(true); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [currentDraftId, dirty, form, artwork, audio]);

  const newDraft = () => {
    setForm({
      song_title: "", artist_name: "", primary_artist_name: "", primary_artist_instagram: "", primary_artist_spotify: "", primary_artist_apple_music: "",
      singer_name: "", composer_name: "", lyricist_name: "", music_director: "", label_name: "",
      deity_name: "", language: "", genre: "", release_date: "", email: user?.email || "", notes: "",
    });
    setArtwork(null); setArtworkPreview(""); setArtworkError("");
    setAudio(null); setErrors({});
    setCurrentDraftId(null);
    setDraftName(""); setLastSaved(null); setDirty(false);
    toast.info("Started a fresh draft");
  };

  const discardCurrentDraft = async () => {
    if (currentDraftId) await removeDraft(currentDraftId);
    newDraft();
    await refreshDrafts();
    toast.success("Draft removed");
  };

  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === "object" ? v.target.value : v });
  const blurTC = (k) => () => setForm((f) => (TITLE_CASE_FIELDS.includes(k) && f[k] ? { ...f, [k]: toTitleCase(f[k]) } : f));

  const [deityOptions, setDeityOptions] = useState(DEITY_NAMES);
  const [addingName, setAddingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    api.get("/deity-names").then((r) => {
      const custom = (r.data?.names || []).filter((n) => !DEITY_NAMES.some((d) => d.toLowerCase() === n.toLowerCase()));
      setDeityOptions([...DEITY_NAMES, ...custom]);
    }).catch(() => {});
  }, []);

  const saveCustomName = async () => {
    const nm = toTitleCase(newName.trim());
    if (!nm) return;
    const existing = deityOptions.find((d) => d.toLowerCase() === nm.toLowerCase());
    if (existing) {
      setForm((f) => ({ ...f, deity_name: existing }));
      setAddingName(false); setNewName("");
      toast.info("Name already exists — selected");
      return;
    }
    setSavingName(true);
    try {
      const r = await api.post("/deity-names", { name: nm });
      const saved = r.data?.name || nm;
      setDeityOptions((o) => [...o, saved]);
      setForm((f) => ({ ...f, deity_name: saved }));
      setAddingName(false); setNewName("");
      toast.success(`Added "${saved}"`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add name");
    } finally { setSavingName(false); }
  };

  const onArt = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setArtworkError("");
    if (!/\.(jpe?g)$/i.test(f.name) || (f.type && !/jpeg|jpg/.test(f.type))) {
      setArtworkError("Artwork must be a JPG/JPEG image with exactly 1500 × 1500 pixel dimensions.");
      toast.error("Artwork must be JPG/JPEG only");
      return;
    }
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth !== 1500 || img.naturalHeight !== 1500) {
        setArtworkError(`Artwork must be exactly 1500 × 1500 pixels (received ${img.naturalWidth} × ${img.naturalHeight}).`);
        setArtwork(null); setArtworkPreview("");
        toast.error("Artwork dimensions must be 1500 × 1500 pixels");
      } else {
        setArtwork(f);
        setArtworkPreview(url);
      }
    };
    img.onerror = () => setArtworkError("Could not read image. Please upload a valid JPG.");
    img.src = url;
  };

  const onAud = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(mp3|wav)$/i.test(f.name)) {
      toast.error("Audio must be a .mp3 or .wav file");
      return;
    }
    setAudio(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      ["song_title", "Song Title"], ["singer_name", "Singer"],
      ["lyricist_name", "Lyricist"], ["music_director", "Music Director"],
      ["label_name", "Label Name"], ["language", "Language"],
    ];
    const nextErrors = {};
    for (const [k, label] of required) {
      if (!(form[k] || "").trim()) nextErrors[k] = `${label} is required`;
    }
    if (!artwork) nextErrors.artwork = "Artwork is required (JPG 1500×1500)";
    if (!audio) nextErrors.audio = "Master Audio is required (MP3 or WAV)";
    setErrors(nextErrors);
    const firstBad = Object.keys(nextErrors)[0];
    if (firstBad) {
      const el = fieldRefs.current[firstBad];
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el?.querySelector?.("input, textarea, [role='combobox']");
      focusable?.focus?.();
      toast.error(nextErrors[firstBad]);
      return;
    }
    setLoading(true); setProgress(0);
    const normalized = { ...form };
    TITLE_CASE_FIELDS.forEach((k) => { if (normalized[k]) normalized[k] = toTitleCase(normalized[k]); });
    const fd = new FormData();
    Object.entries(normalized).forEach(([k, v]) => fd.append(k, v));
    fd.append("artwork", artwork);
    fd.append("audio", audio);
    try {
      await api.post("/submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
      });
      toast.success("Submission received! ISRC/UPC auto-assigned if available.");
      if (currentDraftId) { await removeDraft(currentDraftId); }
      nav("/my-songs");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">New Submission</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight">Upload your <span className="gradient-text">next release</span></h1>
          <p className="mt-2 text-zinc-500 text-sm">Artwork must be JPG/JPEG at exactly 1500 × 1500 px. Audio must be MP3 or WAV.</p>
          {currentDraftId && (
            <div className="mt-3 text-xs text-zinc-500 flex items-center gap-2" data-testid="draft-status">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Editing draft: <strong className="text-cyan-400">{draftName || currentDraftId}</strong>
              {lastSaved && <span>· auto-saved {new Date(lastSaved).toLocaleTimeString()}</span>}
              {dirty && <span className="text-amber-400">· unsaved changes</span>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={draftName} onChange={(e) => setDraftName(e.target.value)}
            placeholder="Draft name (optional)" data-testid="draft-name"
            className="bg-zinc-900 border-zinc-800 w-48 h-9 text-sm" />
          <Button type="button" onClick={() => saveDraft(false)} disabled={savingDraft} variant="outline"
            data-testid="save-draft" className="rounded-full border-zinc-700 hover:bg-zinc-900 text-cyan-400">
            <Save className="w-4 h-4 mr-1.5" /> {savingDraft ? "Saving..." : "Save Draft"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" data-testid="drafts-menu"
                className="rounded-full border-zinc-700 hover:bg-zinc-900">
                Drafts ({drafts.length}) <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800 w-80 max-h-96 overflow-y-auto">
              <DropdownMenuItem onClick={newDraft} data-testid="new-draft" className="text-cyan-400 focus:bg-zinc-900">
                <FilePlus className="w-4 h-4 mr-2" /> New draft
              </DropdownMenuItem>
              <DropdownMenuItem onClick={refreshDrafts} className="focus:bg-zinc-900">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh list
              </DropdownMenuItem>
              {drafts.length > 0 && <DropdownMenuSeparator className="bg-zinc-800" />}
              {drafts.length > 0 && <DropdownMenuLabel className="text-xs text-zinc-500">Saved drafts</DropdownMenuLabel>}
              {drafts.map((d) => (
                <DropdownMenuItem key={d.id} className={`focus:bg-zinc-900 ${currentDraftId === d.id ? "bg-zinc-900" : ""}`}
                  onSelect={(e) => e.preventDefault()} data-testid={`draft-item-${d.id}`}>
                  <div className="flex items-start justify-between w-full gap-2">
                    <button type="button" onClick={() => applyDraft(d.id)} className="flex-1 text-left min-w-0">
                      <div className="truncate font-medium">{d.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {d.song_title || "Untitled"} · {new Date(d.saved_at).toLocaleString()}
                      </div>
                      <div className="text-[10px] text-zinc-600">
                        {d.has_artwork ? "🎨" : "·"} {d.has_audio ? "🎵" : "·"}
                      </div>
                    </button>
                    <button type="button" onClick={async () => { await removeDraft(d.id); if (currentDraftId === d.id) newDraft(); await refreshDrafts(); toast.success("Deleted"); }}
                      data-testid={`draft-del-${d.id}`} className="text-rose-400 hover:text-rose-300 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </DropdownMenuItem>
              ))}
              {drafts.length === 0 && <div className="px-2 py-3 text-xs text-zinc-500 text-center">No saved drafts yet</div>}
            </DropdownMenuContent>
          </DropdownMenu>
          {currentDraftId && (
            <Button type="button" onClick={discardCurrentDraft} variant="outline"
              data-testid="discard-draft" className="rounded-full border-zinc-700 hover:bg-zinc-900 text-rose-400">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete Current
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="space-y-8">
        <aside data-testid="upload-tat-notice"
          className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 sm:p-5 flex gap-3 items-start">
          <div className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-cyan-400 shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-cyan-400 font-semibold">
              Processing &amp; Delivery Information
            </div>
            <p className="mt-1.5 text-sm text-zinc-300 leading-relaxed">
              The official turnaround time (TAT) is at least <b>48 hours</b> with all DSPs, although content may be delivered faster during non-peak periods.
              If your release is not delivered within the expected TAT, please contact the <b>Sonotek Team</b> and we will escalate the issue with the respective DSP.
            </p>
          </div>
        </aside>

        <section className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-cyan-400" /> Media</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div ref={(el) => (fieldRefs.current.artwork = el)}>
              <Label className={`text-xs uppercase tracking-wider ${errors.artwork ? "text-rose-400" : "text-zinc-400"}`}>Artwork (JPG · 1500×1500) <span className="text-rose-400 ml-1">*</span></Label>
              <div className={`mt-2 relative border-2 border-dashed rounded-xl aspect-square bg-zinc-900/40 transition-colors cursor-pointer overflow-hidden ${errors.artwork ? "border-rose-500/60" : "border-zinc-800 hover:border-cyan-500/50"}`}
                onClick={() => artInput.current?.click()} data-testid="artwork-drop">
                {artworkPreview ? (
                  <>
                    <img src={artworkPreview} alt="preview" className="w-full h-full object-cover" />
                    <button type="button" onClick={(e) => { e.stopPropagation(); setArtwork(null); setArtworkPreview(""); setArtworkError(""); }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-zinc-950/80 hover:bg-rose-500/80 flex items-center justify-center" data-testid="artwork-remove">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-2 left-2 right-2 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs">
                      ✓ 1500 × 1500 JPG
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 p-4 text-center">
                    <ImageIcon className="w-8 h-8 mb-2" />
                    <div className="text-sm">Click to upload artwork</div>
                    <div className="text-xs mt-1">JPG/JPEG · exactly 1500 × 1500 px</div>
                  </div>
                )}
                <input ref={artInput} type="file" accept=".jpg,.jpeg,image/jpeg" onChange={onArt} className="hidden" data-testid="artwork-input" />
              </div>
              {artworkError && (
                <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs" data-testid="artwork-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {artworkError}
                </div>
              )}
            </div>
            <div ref={(el) => (fieldRefs.current.audio = el)}>
              <Label className={`text-xs uppercase tracking-wider ${errors.audio ? "text-rose-400" : "text-zinc-400"}`}>Master Audio (.mp3 / .wav) <span className="text-rose-400 ml-1">*</span></Label>
              <div className={`mt-2 border-2 border-dashed rounded-xl aspect-square bg-zinc-900/40 transition-colors cursor-pointer flex flex-col items-center justify-center p-6 ${errors.audio ? "border-rose-500/60" : "border-zinc-800 hover:border-cyan-500/50"}`}
                onClick={() => audInput.current?.click()} data-testid="audio-drop">
                <FileAudio className="w-10 h-10 text-zinc-500 mb-3" />
                {audio ? (
                  <div className="text-center">
                    <div className="font-medium text-sm truncate max-w-[200px]">{audio.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{(audio.size / 1024 / 1024).toFixed(2)} MB</div>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setAudio(null); }}
                      className="mt-3 text-xs text-rose-400 hover:underline" data-testid="audio-remove">Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm">Click to upload master</div>
                    <div className="text-xs mt-1 text-zinc-500">MP3 or WAV up to studio quality</div>
                  </>
                )}
                <input ref={audInput} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" onChange={onAud} className="hidden" data-testid="audio-input" />
              </div>
            </div>
          </div>
        </section>

        <section className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
          <h2 className="text-lg font-semibold mb-6">Primary Artist</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Primary Artist Name"><Input data-testid="field-primary_artist_name" value={form.primary_artist_name} onChange={set("primary_artist_name")} onBlur={blurTC("primary_artist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Instagram (optional)">
              <div className="relative">
                <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="field-primary_artist_instagram" value={form.primary_artist_instagram} onChange={set("primary_artist_instagram")} placeholder="@handle or URL" className="bg-zinc-900 border-zinc-800 pl-9" />
              </div>
            </Field>
            <Field label="Spotify Artist (optional)"><Input data-testid="field-primary_artist_spotify" value={form.primary_artist_spotify} onChange={set("primary_artist_spotify")} placeholder="Artist URL or ID" className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Apple Music Artist (optional)"><Input data-testid="field-primary_artist_apple_music" value={form.primary_artist_apple_music} onChange={set("primary_artist_apple_music")} placeholder="Artist URL or ID" className="bg-zinc-900 border-zinc-800" /></Field>
          </div>
        </section>

        <section className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
          <h2 className="text-lg font-semibold mb-6">Release Metadata</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Song Title" required error={errors.song_title} innerRef={(el) => (fieldRefs.current.song_title = el)}><Input data-testid="field-song_title" value={form.song_title} onChange={set("song_title")} onBlur={blurTC("song_title")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Featured Artist(s)"><Input data-testid="field-artist_name" value={form.artist_name} onChange={set("artist_name")} onBlur={blurTC("artist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Singer" required error={errors.singer_name} innerRef={(el) => (fieldRefs.current.singer_name = el)}><Input data-testid="field-singer_name" value={form.singer_name} onChange={set("singer_name")} onBlur={blurTC("singer_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Composer"><Input data-testid="field-composer_name" value={form.composer_name} onChange={set("composer_name")} onBlur={blurTC("composer_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Lyricist" required error={errors.lyricist_name} innerRef={(el) => (fieldRefs.current.lyricist_name = el)}><Input data-testid="field-lyricist_name" value={form.lyricist_name} onChange={set("lyricist_name")} onBlur={blurTC("lyricist_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Music Director" required error={errors.music_director} innerRef={(el) => (fieldRefs.current.music_director = el)}><Input data-testid="field-music_director" value={form.music_director} onChange={set("music_director")} onBlur={blurTC("music_director")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Label Name" required error={errors.label_name} innerRef={(el) => (fieldRefs.current.label_name = el)}><Input data-testid="field-label_name" value={form.label_name} onChange={set("label_name")} onBlur={blurTC("label_name")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Language" required error={errors.language} innerRef={(el) => (fieldRefs.current.language = el)}>
              <Select value={form.language} onValueChange={set("language")}>
                <SelectTrigger data-testid="field-language" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select language" /></SelectTrigger>
                <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Genre">
              <Select value={form.genre} onValueChange={set("genre")}>
                <SelectTrigger data-testid="field-genre" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select genre" /></SelectTrigger>
                <SelectContent>{GENRES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sub Genre">
              <Select value={form.deity_name} onValueChange={(v) => { if (v === "__add_name__") { setAddingName(true); return; } if (!v) return; setForm((f) => ({ ...f, deity_name: v })); }}>
                <SelectTrigger data-testid="field-deity_name" className="bg-zinc-900 border-zinc-800">
                  <span className={form.deity_name ? "text-zinc-100" : "text-zinc-500"}>{form.deity_name || "Select sub genre"}</span>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__add_name__" data-testid="deity-add-name" className="text-cyan-400 font-medium">+ Add Name</SelectItem>
                  {deityOptions.map((n) => <SelectItem key={n} value={n} data-testid={`deity-opt-${n.replace(/\s+/g, "-").toLowerCase()}`}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              {addingName && (
                <div className="mt-2 flex items-center gap-2" data-testid="deity-add-row">
                  <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                    onBlur={() => setNewName((v) => toTitleCase(v.trim()))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveCustomName(); } }}
                    placeholder="Enter new name" data-testid="deity-new-name" className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
                  <Button type="button" size="sm" onClick={saveCustomName} disabled={savingName} data-testid="deity-save-name"
                    className="h-9 bg-cyan-500 text-zinc-950 hover:bg-cyan-400 font-semibold">{savingName ? "..." : "Save"}</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => { setAddingName(false); setNewName(""); }}
                    data-testid="deity-cancel-name" className="h-9 border-zinc-700">Cancel</Button>
                </div>
              )}
            </Field>
            <Field label="Release Date"><Input type="date" data-testid="field-release_date" value={form.release_date} onChange={set("release_date")} className="bg-zinc-900 border-zinc-800" /></Field>
            <Field label="Email"><Input type="email" data-testid="field-email" value={form.email} onChange={set("email")} className="bg-zinc-900 border-zinc-800" /></Field>
          </div>
          <div className="mt-5">
            <Field label="Additional Notes">
              <Textarea rows={3} data-testid="field-notes" value={form.notes} onChange={set("notes")} className="bg-zinc-900 border-zinc-800" placeholder="Anything the review team should know..." />
            </Field>
          </div>
          <div className="mt-5 p-4 rounded-lg bg-cyan-500/5 border border-cyan-500/30 text-xs text-cyan-300">
            <strong>Note:</strong> ISRC and UPC codes are auto-assigned by SonotekDigi from the reserved code pool after submission. Users cannot manually set these values.
          </div>
        </section>

        {loading && (
          <div className="p-4 rounded-2xl border border-zinc-800 bg-zinc-950/60">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-zinc-400">Uploading files...</span>
              <span className="text-cyan-400 font-semibold">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-full border-zinc-700 hover:bg-zinc-900" data-testid="upload-cancel">Cancel</Button>
          <Button type="submit" disabled={loading} className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110 px-8" data-testid="upload-submit">
            <Upload className="w-4 h-4 mr-2" /> {loading ? "Submitting..." : "Submit for Review"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children, error, innerRef }) {
  return (
    <div ref={innerRef}>
      <Label className={`text-xs uppercase tracking-wider ${error ? "text-rose-400" : "text-zinc-400"}`}>
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </Label>
      <div className={`mt-2 ${error ? "ring-1 ring-rose-500/60 rounded-md" : ""}`}>{children}</div>
      {error && <div className="mt-1 text-xs text-rose-400 flex items-center gap-1"><span>•</span>{error}</div>}
    </div>
  );
}
